import { motion } from 'framer-motion';

import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { Meta, breadcrumbLd } from '../seo/Meta.jsx';
import { useLenis } from '../anim/useLenis.js';

export default function Docs() {
  useLenis();
  return (
    <>
      <Meta
        title="Quickstart  -  three minutes from key to first JSON"
        description="tender-api REST + SSE quickstart. Authenticate, list tenders by country/CPV/value, stream new tenders, attach watchlists with HMAC-signed webhooks, drop to verbatim eForms UBL XML."
        path="/docs"
        jsonLd={[
          breadcrumbLd([
            { name: 'tender-api', path: '/' },
            { name: 'Docs', path: '/docs' },
          ]),
        ]}
      />
      <Header />
      <main className="container" style={{ paddingTop: 64, paddingBottom: 96, maxWidth: 880 }}>
        <h1 className="hero-headline mono" style={{ fontSize: 'clamp(36px, 5vw, 60px)' }}>
          Quickstart
        </h1>
        <p className="lede" style={{ marginTop: 16, maxWidth: 600 }}>
          Three minutes from API key to first JSON payload.
        </p>

        <Section heading="1. Authenticate" anchor="auth">
          <p>
            Every request to <code>/v1/*</code> must carry your API key. Pass it as
            either <code>X-Api-Key</code> or <code>Authorization: Bearer ow_…</code>.
          </p>
          <pre>
{`curl -H "X-Api-Key: \${OMNI_KEY}" \\
  https://tender-api.com/v1/eu-tenders/tenders?limit=5`}
          </pre>
        </Section>

        <Section heading="2. List tenders" anchor="list">
          <p>
            Filter by country, CPV code, value bracket, buyer name, or keyword.
            Results are paginated via opaque cursors.
          </p>
          <pre>
{`# 25 most recent IT-related tenders worth >€100k
curl -H "X-Api-Key: \${OMNI_KEY}" \\
  "https://tender-api.com/v1/eu-tenders/tenders?\\
country=DE,FR,NL&\\
cpv=72000000&\\
min_value=100000&\\
limit=25"`}
          </pre>
        </Section>

        <Section heading="3. Stream new tenders" anchor="stream">
          <p>
            Server-Sent Events. Reconnect with <code>Last-Event-ID</code> for
            at-least-once replay over the last 5 minutes.
          </p>
          <pre>
{`curl -N -H "X-Api-Key: \${OMNI_KEY}" \\
  https://tender-api.com/v1/eu-tenders/stream`}
          </pre>
        </Section>

        <Section heading="4. Watchlists + webhooks" anchor="watchlists">
          <p>
            Define a filter once, get pinged via signed webhook every time a new
            tender matches. HMAC-SHA256 over <code>timestamp.body</code>; retries
            follow the schedule in ADR 0016.
          </p>
          <pre>
{`# Create a watchlist
curl -X POST -H "X-Api-Key: \${OMNI_KEY}" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"FR construction","filter":{"country":"FR","cpv":"45000000"}}' \\
  https://tender-api.com/v1/watchlists

# Attach a webhook subscription
curl -X POST -H "X-Api-Key: \${OMNI_KEY}" \\
  -H "Content-Type: application/json" \\
  -d '{"url":"https://your-app.example.com/hooks/tender-api","secret":"…"}' \\
  https://tender-api.com/v1/webhook-subscriptions`}
          </pre>
        </Section>

        <Section heading="5. Need verbatim raw upstream?" anchor="raw">
          <p>
            Every tender has a sibling <code>/raw</code> endpoint that returns the
            unmodified upstream payload (eForms UBL XML for EU notices, JSON
            projection for TED, OCDS extract for OGP, etc.). ADR 0046.
          </p>
          <pre>
{`curl -H "X-Api-Key: \${OMNI_KEY}" \\
  https://tender-api.com/v1/eu-tenders/tenders/<id>/raw`}
          </pre>
        </Section>

        <p className="mono" style={{ marginTop: 64, fontSize: 12, color: 'var(--fg-3)' }}>
          Full OpenAPI spec generated per ADR 0033  -  coming with the GA launch. Until
          then this quickstart + the JSON shape on the live sample page is the
          contract.
        </p>
      </main>
      <Footer />
    </>
  );
}

function Section({ heading, anchor, children }) {
  return (
    <motion.section
      id={anchor}
      style={{ marginTop: 64 }}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-15%' }}
      transition={{ duration: 0.6, ease: [0.7, 0, 0.15, 1] }}
    >
      <h2 className="mono" style={{ fontSize: 28, fontWeight: 500, letterSpacing: '-0.01em', margin: '0 0 12px', color: 'var(--fg-0)' }}>
        {heading}
      </h2>
      {children}
    </motion.section>
  );
}
